import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  Card,
  CardTitle,
  CardContent,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn
} from '../styles/PageStyles';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';

// Styled components for the tools page
const ToolCard = styled(Card)`
  margin-bottom: 1.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const ToolHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ToolTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const ToolStatus = styled.div`
  font-size: 0.8rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  background: ${props => props.status === 'active' ? 'rgba(52, 199, 89, 0.1)' :
    props.status === 'beta' ? 'rgba(255, 149, 0, 0.1)' :
      props.status === 'deprecated' ? 'rgba(255, 59, 48, 0.1)' : 'rgba(142, 142, 147, 0.1)'};
  color: ${props => props.status === 'active' ? '#34C759' :
    props.status === 'beta' ? '#FF9500' :
      props.status === 'deprecated' ? '#FF3B30' : 'var(--text-secondary)'};
`;

const ToolTags = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const ToolTag = styled.div`
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  background: rgba(0, 102, 255, 0.05);
  color: var(--primary);
`;

const ToolDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const ToolMetadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const ToolMetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToolCode = styled.pre`
  background: #f8f9fa;
  border-radius: 6px;
  padding: 1rem;
  font-family: 'Roboto Mono', monospace;
  font-size: 0.85rem;
  overflow-x: auto;
  margin-bottom: 1.5rem;
  border: 1px solid #eaeaea;
`;

const ToolFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const ToolActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? 'var(--primary)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--text-primary)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : '#e0e0e0'};
  border-radius: 20px;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary)' : 'rgba(0, 102, 255, 0.05)'};
  }
`;

const Tools = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');

  // Mock data for tools
  const tools = [
    {
      id: 'tool-001',
      name: 'Document Analyzer',
      description: 'Extracts key information from documents including entities, sentiment, and structured data.',
      type: 'API',
      status: 'active',
      tags: ['Document Processing', 'NLP', 'Extraction'],
      endpoint: '/api/tools/document-analyzer',
      authRequired: true,
      inputFormat: 'JSON',
      outputFormat: 'JSON',
      latency: '~300ms',
      rateLimit: '100 calls/min',
      lastUpdated: '2 days ago',
      version: 'v2.3.1',
      owner: 'Document Processing Team',
      usage: {
        count: '12,456',
        successRate: '99.2%',
        avgResponseTime: '285ms'
      },
      example: `{
  "document": "base64_encoded_document",
  "extractionTypes": ["entities", "sentiment", "tables"],
  "outputFormat": "json"
}`
    },
    {
      id: 'tool-002',
      name: 'Policy Validator',
      description: 'Validates insurance policies against regulatory requirements and internal rules.',
      type: 'Code Execution',
      status: 'active',
      tags: ['Compliance', 'Validation', 'Rules Engine'],
      endpoint: 'internal://policy-validator',
      authRequired: true,
      inputFormat: 'JSON',
      outputFormat: 'JSON',
      latency: '~500ms',
      rateLimit: '50 calls/min',
      lastUpdated: '1 week ago',
      version: 'v1.5.0',
      owner: 'Compliance Team',
      usage: {
        count: '8,932',
        successRate: '98.7%',
        avgResponseTime: '485ms'
      },
      example: `{
  "policyId": "POL-12345",
  "jurisdiction": "CA",
  "policyType": "auto",
  "validationLevel": "strict"
}`
    },
    {
      id: 'tool-003',
      name: 'Fraud Detection',
      description: 'Analyzes claims for potential fraud indicators using machine learning algorithms.',
      type: 'ML Model',
      status: 'active',
      tags: ['Fraud', 'ML', 'Risk Assessment'],
      endpoint: '/api/tools/fraud-detection',
      authRequired: true,
      inputFormat: 'JSON',
      outputFormat: 'JSON',
      latency: '~800ms',
      rateLimit: '30 calls/min',
      lastUpdated: '5 days ago',
      version: 'v3.2.0',
      owner: 'Fraud Team',
      usage: {
        count: '7,845',
        successRate: '97.5%',
        avgResponseTime: '756ms'
      },
      example: `{
  "claimId": "CLM-67890",
  "claimantInfo": { ... },
  "incidentDetails": { ... },
  "detectionLevel": "comprehensive"
}`
    },
    {
      id: 'tool-004',
      name: 'Agent Connector',
      description: 'Enables communication between different agents in a pipeline, handling message passing and state management.',
      type: 'Agent',
      status: 'beta',
      tags: ['Agent Communication', 'Orchestration', 'State Management'],
      endpoint: 'agent://connector',
      authRequired: true,
      inputFormat: 'JSON',
      outputFormat: 'JSON',
      latency: '~150ms',
      rateLimit: '200 calls/min',
      lastUpdated: '3 days ago',
      version: 'v0.9.2',
      owner: 'Agent Platform Team',
      usage: {
        count: '25,678',
        successRate: '96.8%',
        avgResponseTime: '142ms'
      },
      example: `{
  "sourceAgentId": "agent-001",
  "targetAgentId": "agent-002",
  "messageType": "request",
  "payload": { ... },
  "priority": "high"
}`
    },
    {
      id: 'tool-005',
      name: 'External Notification',
      description: 'Sends notifications to external systems via webhooks, email, SMS, or push notifications.',
      type: 'Webhook',
      status: 'active',
      tags: ['Notifications', 'Integration', 'Communication'],
      endpoint: '/api/tools/external-notification',
      authRequired: true,
      inputFormat: 'JSON',
      outputFormat: 'JSON',
      latency: '~200ms',
      rateLimit: '100 calls/min',
      lastUpdated: '1 day ago',
      version: 'v2.0.1',
      owner: 'Integration Team',
      usage: {
        count: '32,456',
        successRate: '99.5%',
        avgResponseTime: '195ms'
      },
      example: `{
  "channel": "webhook",
  "destination": "https://example.com/webhook",
  "message": {
    "title": "Claim Approved",
    "body": "Claim #CLM-12345 has been approved",
    "data": { ... }
  },
  "priority": "normal"
}`
    },
    {
      id: 'tool-006',
      name: 'Knowledge Base Query',
      description: 'Searches the knowledge base for relevant information using semantic search and retrieval.',
      type: 'API',
      status: 'active',
      tags: ['Knowledge Base', 'Search', 'Retrieval'],
      endpoint: '/api/tools/kb-query',
      authRequired: true,
      inputFormat: 'JSON',
      outputFormat: 'JSON',
      latency: '~400ms',
      rateLimit: '60 calls/min',
      lastUpdated: '4 days ago',
      version: 'v2.1.0',
      owner: 'Knowledge Management Team',
      usage: {
        count: '18,932',
        successRate: '98.9%',
        avgResponseTime: '385ms'
      },
      example: `{
  "query": "What is the procedure for handling water damage claims?",
  "filters": {
    "documentTypes": ["policy", "procedure"],
    "tags": ["water damage", "claims processing"]
  },
  "limit": 5,
  "includeMetadata": true
}`
    },
    {
      id: 'tool-007',
      name: 'Data Transformation',
      description: 'Transforms data between different formats and structures using configurable mapping rules.',
      type: 'Code Execution',
      status: 'active',
      tags: ['Data Processing', 'Transformation', 'Integration'],
      endpoint: 'internal://data-transformer',
      authRequired: true,
      inputFormat: 'Any',
      outputFormat: 'Any',
      latency: '~250ms',
      rateLimit: '80 calls/min',
      lastUpdated: '1 week ago',
      version: 'v1.8.2',
      owner: 'Data Engineering Team',
      usage: {
        count: '22,456',
        successRate: '99.1%',
        avgResponseTime: '245ms'
      },
      example: `{
  "input": { ... },
  "inputFormat": "json",
  "outputFormat": "xml",
  "mappingRules": [
    { "source": "customer.name", "target": "Client.Name" },
    { "source": "customer.id", "target": "Client.ID" }
  ]
}`
    },
    {
      id: 'tool-008',
      name: 'Legacy System Connector',
      description: 'Provides a modern API interface to legacy systems, handling protocol translation and data mapping.',
      type: 'API',
      status: 'active',
      tags: ['Legacy', 'Integration', 'Compatibility'],
      endpoint: '/api/tools/legacy-connector',
      authRequired: true,
      inputFormat: 'JSON',
      outputFormat: 'JSON',
      latency: '~700ms',
      rateLimit: '20 calls/min',
      lastUpdated: '2 weeks ago',
      version: 'v1.3.4',
      owner: 'Legacy Systems Team',
      usage: {
        count: '5,678',
        successRate: '97.2%',
        avgResponseTime: '685ms'
      },
      example: `{
  "system": "claims_1990",
  "operation": "getClaim",
  "parameters": {
    "claimId": "12345",
    "includeHistory": true
  }
}`
    }
  ];

  // Filter tools based on search term and active filter
  const filteredTools = tools.filter(tool => {
    const matchesSearch =
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));

    if (activeFilter === 'all') return matchesSearch;
    return matchesSearch && (
      (activeFilter === 'api' && tool.type === 'API') ||
      (activeFilter === 'code' && tool.type === 'Code Execution') ||
      (activeFilter === 'agent' && tool.type === 'Agent') ||
      (activeFilter === 'webhook' && tool.type === 'Webhook') ||
      (activeFilter === 'ml' && tool.type === 'ML Model')
    );
  });

  return (
    <PageContainer>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
      >
        <PageHeaderWithInfo
          icon={faScrewdriverWrench}
          title="Agent Tools"
          description="Tools are reusable components that agents can call to perform specific tasks within pipelines.
          They include APIs, code execution, ML models, webhooks, and connections to other agents."
        >
          <div style={{ display: 'flex', gap: '1rem' }}>
            <SearchInput
              type="text"
              placeholder="Search tools..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '300px' }}
            />
            <Button primary>Create Tool</Button>
          </div>
        </PageHeaderWithInfo>


        <FilterBar>
          <FilterButton active={activeFilter === 'all'} onClick={() => setActiveFilter('all')}>All Types</FilterButton>
          <FilterButton active={activeFilter === 'api'} onClick={() => setActiveFilter('api')}>API</FilterButton>
          <FilterButton active={activeFilter === 'code'} onClick={() => setActiveFilter('code')}>Code Execution</FilterButton>
          <FilterButton active={activeFilter === 'agent'} onClick={() => setActiveFilter('agent')}>Agent</FilterButton>
          <FilterButton active={activeFilter === 'webhook'} onClick={() => setActiveFilter('webhook')}>Webhook</FilterButton>
          <FilterButton active={activeFilter === 'ml'} onClick={() => setActiveFilter('ml')}>ML Model</FilterButton>
        </FilterBar>

        <TabContainer>
          <TabList>
            <Tab active={activeTab === 'all'} onClick={() => setActiveTab('all')}>All Tools</Tab>
            <Tab active={activeTab === 'document'} onClick={() => setActiveTab('document')}>Document Processing</Tab>
            <Tab active={activeTab === 'integration'} onClick={() => setActiveTab('integration')}>Integration</Tab>
            <Tab active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>Analytics</Tab>
          </TabList>

          <TabContent active={true}>
            {filteredTools.map(tool => (
              <ToolCard key={tool.id}>
                <ToolHeader>
                  <ToolTitle>{tool.name}</ToolTitle>
                  <ToolStatus status={tool.status}>
                    {tool.status.charAt(0).toUpperCase() + tool.status.slice(1)}
                  </ToolStatus>
                </ToolHeader>

                <ToolTags>
                  <ToolTag>{tool.type}</ToolTag>
                  {tool.tags.map((tag, index) => (
                    <ToolTag key={index}>{tag}</ToolTag>
                  ))}
                </ToolTags>

                <ToolDescription>
                  {tool.description}
                </ToolDescription>

                <ToolMetadata>
                  <ToolMetadataItem>
                    <strong>Endpoint:</strong> {tool.endpoint}
                  </ToolMetadataItem>
                  <ToolMetadataItem>
                    <strong>Auth Required:</strong> {tool.authRequired ? 'Yes' : 'No'}
                  </ToolMetadataItem>
                  <ToolMetadataItem>
                    <strong>Input:</strong> {tool.inputFormat}
                  </ToolMetadataItem>
                  <ToolMetadataItem>
                    <strong>Output:</strong> {tool.outputFormat}
                  </ToolMetadataItem>
                  <ToolMetadataItem>
                    <strong>Latency:</strong> {tool.latency}
                  </ToolMetadataItem>
                  <ToolMetadataItem>
                    <strong>Rate Limit:</strong> {tool.rateLimit}
                  </ToolMetadataItem>
                  <ToolMetadataItem>
                    <strong>Version:</strong> {tool.version}
                  </ToolMetadataItem>
                </ToolMetadata>

                <CardTitle>Example Request</CardTitle>
                <ToolCode>{tool.example}</ToolCode>

                <ToolFooter>
                  <div style={{ display: 'flex', gap: '2rem' }}>
                    <div>Last updated: {tool.lastUpdated}</div>
                    <div>Owner: {tool.owner}</div>
                    <div>Usage: {tool.usage.count} calls</div>
                    <div>Success Rate: {tool.usage.successRate}</div>
                  </div>

                  <ToolActions>
                    <Button>Documentation</Button>
                    <Button>Test</Button>
                    <Button primary>Use in Pipeline</Button>
                  </ToolActions>
                </ToolFooter>
              </ToolCard>
            ))}
          </TabContent>
        </TabContainer>
      </motion.div>
    </PageContainer>
  );
};

export default Tools;
